import { supabase } from '@/lib/supabase';
import { getAppBaseUrl } from '@/lib/capacitor';

export interface PartnerConnection {
  id: string;
  pregnancy_id: string;
  user_1_id: string;
  user_2_id: string | null;
  invitation_token: string;
  status: 'pending' | 'active' | 'removed';
  connected_at: string | null;
  created_at: string;
}

export interface PartnerProfile {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
}

/**
 * Gültigkeitsdauer einer ausstehenden Einladung in Tagen.
 *
 * Der Ablauf wird ausschliesslich aus created_at abgeleitet - es gibt weder
 * ein Ablauffeld noch einen Status 'expired' und keinen Cron-Job. Eine
 * abgelaufene Einladung bleibt in der Datenbank auf status='pending' stehen
 * und wird als ungültig behandelt.
 *
 * Hier steuert der Wert nur die Anzeige und die Frage, ob eine neue Einladung
 * erstellt werden darf. Beim Einlösen entscheidet accept_partner_invite()
 * (Migration 166) - dort steht dieselbe Frist als interval '7 days'.
 */
export const INVITE_VALIDITY_DAYS = 7;

const INVITE_VALIDITY_MS = INVITE_VALIDITY_DAYS * 24 * 60 * 60 * 1000;

/** Fehlercode für eine Einladung, deren Gültigkeit abgelaufen ist */
export const INVITE_EXPIRED = 'INVITE_EXPIRED';
/** Fehlercode für eine bereits bestehende aktive Partner-Verbindung */
export const ALREADY_CONNECTED = 'ALREADY_CONNECTED';
/** Fehlercode für einen unbekannten oder bereits eingelösten Token */
export const INVITE_NOT_FOUND = 'INVITE_NOT_FOUND';

/**
 * Zeitpunkt, zu dem eine am angegebenen Datum erstellte Einladung abläuft
 */
export function getInviteExpiresAt(createdAt: string): Date {
  return new Date(new Date(createdAt).getTime() + INVITE_VALIDITY_MS);
}

/**
 * Formatiert den Ablaufzeitpunkt einer Einladung für die Anzeige
 */
export function formatInviteExpiry(expiresAt: Date, locale: string): string {
  return expiresAt.toLocaleString(locale === 'en' ? 'en-US' : 'de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Prüft, ob eine Einladung älter als INVITE_VALIDITY_DAYS ist
 */
export function isInviteExpired(connection: { created_at: string } | null | undefined): boolean {
  if (!connection?.created_at) return false;
  return getInviteExpiresAt(connection.created_at).getTime() <= Date.now();
}

/**
 * Prüft, ob eine Verbindung eine gültige, noch einlösbare Einladung ist
 */
export function isInvitePending(
  connection: { status: string; created_at: string } | null | undefined
): boolean {
  return connection?.status === 'pending' && !isInviteExpired(connection);
}

/**
 * Wählt aus mehreren Verbindungen einer Schwangerschaft die relevante aus.
 *
 * Historisch sind pro Schwangerschaft mehrere Zeilen entstanden (siehe
 * createPartnerInvite). Ein einzelnes .maybeSingle() liefert in dem Fall einen
 * Fehler statt Daten, wodurch eine bestehende Einladung unsichtbar wurde und
 * bei jedem Klick eine weitere Zeile entstand. Deshalb wird hier bewusst über
 * mehrere Zeilen sortiert: aktive Verbindung schlägt Einladung, sonst gewinnt
 * die jüngste Zeile.
 */
function pickRelevantConnection<T extends { status: string; created_at: string }>(
  rows: T[] | null
): T | null {
  if (!rows?.length) return null;

  // rows kommen bereits nach created_at absteigend sortiert an
  const byStatus = (status: string) => rows.find((row) => row.status === status) || null;

  return byStatus('active') || byStatus('pending') || byStatus('removed') || null;
}

/**
 * Generate a unique invitation token
 */
function generateInviteToken(): string {
  return crypto.randomUUID().replace(/-/g, '').substring(0, 16);
}

interface CreatePartnerInviteOptions {
  /** Wird beim Anlegen aus dem Onboarding gesetzt (Mutter = primary) */
  primaryUserId?: string | null;
}

/**
 * Create a partner invitation for a pregnancy
 *
 * Existiert bereits eine gültige ausstehende Einladung, wird diese
 * zurückgegeben statt eine zweite anzulegen. Eine
 * abgelaufene oder abgebrochene Einladung gilt nicht als bestehend - ihre
 * Zeile wird mit neuem Token und neuem created_at wiederverwendet.
 */
export async function createPartnerInvite(
  pregnancyId: string,
  userId: string,
  options: CreatePartnerInviteOptions = {}
) {
  const invitationToken = generateInviteToken();

  // Alle Verbindungen dieses Nutzers zu dieser Schwangerschaft laden.
  // Kein .maybeSingle(): das liefert bei mehreren Zeilen einen Fehler statt
  // Daten und würde die Duplikatsprüfung wirkungslos machen.
  const { data: rows } = await supabase
    .from('partner_connections')
    .select('*')
    .eq('pregnancy_id', pregnancyId)
    .eq('user_1_id', userId)
    .order('created_at', { ascending: false })
    .limit(10);

  const existing = pickRelevantConnection(rows);

  if (existing) {
    // Already has active partner
    if (existing.status === 'active') {
      return {
        data: null,
        error: { message: 'Partner bereits verbunden', code: ALREADY_CONNECTED },
      };
    }

    // Gültige Einladung vorhanden - keine zweite erstellen
    if (isInvitePending(existing)) {
      return { data: existing, error: null };
    }

    // Abgelaufene oder abgebrochene Einladung: Zeile wiederverwenden.
    // created_at muss dabei zurückgesetzt werden, sonst wäre die neue
    // Einladung sofort wieder abgelaufen.
    if (existing.status === 'pending' || existing.status === 'removed') {
      const { data, error } = await supabase
        .from('partner_connections')
        .update({
          invitation_token: invitationToken,
          status: 'pending',
          user_2_id: null,
          connected_at: null,
          created_at: new Date().toISOString(),
          ...(options.primaryUserId !== undefined
            ? { primary_user_id: options.primaryUserId }
            : {}),
        })
        .eq('id', existing.id)
        .select()
        .single();

      return { data, error };
    }
  }

  // Create new invitation
  const { data, error } = await supabase
    .from('partner_connections')
    .insert({
      pregnancy_id: pregnancyId,
      user_1_id: userId,
      invitation_token: invitationToken,
      status: 'pending',
      ...(options.primaryUserId !== undefined
        ? { primary_user_id: options.primaryUserId }
        : {}),
    })
    .select()
    .single();

  return { data, error };
}

/**
 * Get invite details by token (for invite page)
 * Uses RPC function with SECURITY DEFINER to allow anon access
 *
 * get_invite_by_token() filtert nur auf status='pending'. Die 7-Tage-Regel
 * wird bewusst hier in der App ausgewertet - die RPC liefert created_at mit.
 */
export async function getInviteByToken(token: string) {
  const { data, error } = await supabase.rpc('get_invite_by_token', {
    invite_token: token,
  });

  if (error) {
    return { data: null, error };
  }

  if (!data) {
    return {
      data: null,
      error: { message: 'Einladung nicht gefunden oder bereits verwendet', code: INVITE_NOT_FOUND },
    };
  }

  if (isInviteExpired(data)) {
    return {
      data: null,
      error: { message: 'Einladung abgelaufen', code: INVITE_EXPIRED },
    };
  }

  return { data, error: null };
}

/** Fehlercodes, die accept_partner_invite() zurueckgeben kann */
const ACCEPT_ERROR_MESSAGES: Record<string, string> = {
  NOT_AUTHENTICATED: 'Bitte melde dich an, um die Einladung anzunehmen',
  INVITE_NOT_FOUND: 'Einladung nicht gefunden oder bereits verwendet',
  INVITE_EXPIRED: 'Einladung abgelaufen',
  SELF_INVITE: 'Du kannst deine eigene Einladung nicht annehmen',
  ALREADY_CONNECTED:
    'Du hast bereits eine aktive Partner-Verbindung. Bitte trenne zuerst die bestehende Verbindung, bevor du eine neue Einladung annimmst.',
};

/**
 * Accept a partner invitation
 *
 * Laeuft ueber die RPC accept_partner_invite() (SECURITY DEFINER, Migration
 * 166). Direkt auf der Tabelle ginge es nicht: der Annehmende ist vor dem
 * Annehmen weder user_1_id noch user_2_id und sieht die Zeile deshalb nicht.
 * Da Postgres bei einem UPDATE mit Spaltenbezug in WHERE/RETURNING auch die
 * SELECT-Policies auswertet, traefe ein UPDATE aus der App null Zeilen.
 * Ablauf, Selbstannahme und die Pruefung auf eine bestehende Verbindung
 * liegen damit ebenfalls in der Funktion.
 */
export async function acceptPartnerInvite(token: string) {
  const { data, error } = await supabase.rpc('accept_partner_invite', {
    invite_token: token,
  });

  if (error) {
    return { data: null, error };
  }

  if (!data?.success) {
    const code: string = data?.error || INVITE_NOT_FOUND;
    return {
      data: null,
      error: { message: ACCEPT_ERROR_MESSAGES[code] || ACCEPT_ERROR_MESSAGES.INVITE_NOT_FOUND, code },
    };
  }

  return { data: data.connection, error: null };
}

/**
 * Get partner connection for a pregnancy
 */
export async function getPartnerConnection(pregnancyId: string) {
  // First, get the connection.
  // Kein .maybeSingle(): bei mehreren Zeilen lieferte das einen Fehler, die
  // Seite zeigte daraufhin wieder "Einladung erstellen" an und der nächste
  // Klick legte eine weitere Zeile an.
  const { data: rows, error: connectionError } = await supabase
    .from('partner_connections')
    .select('*')
    .eq('pregnancy_id', pregnancyId)
    .in('status', ['pending', 'active'])
    .order('created_at', { ascending: false })
    .limit(10);

  if (connectionError) {
    console.error('[partnerService] Error fetching connection:', connectionError);
    return { data: null, error: connectionError };
  }

  const connection = pickRelevantConnection(rows);

  if (!connection) {
    return { data: null, error: null };
  }

  // Use get_partner_profile RPC which auto-creates missing profiles from auth.users
  const partner1Result = await supabase.rpc('get_partner_profile', {
    partner_user_id: connection.user_1_id,
  });

  if (partner1Result.error) {
    console.error('[partnerService] Error fetching partner 1 profile:', partner1Result.error);
  }

  let partner2Result = null;
  if (connection.user_2_id) {
    partner2Result = await supabase.rpc('get_partner_profile', {
      partner_user_id: connection.user_2_id,
    });

    if (partner2Result.error) {
      console.error('[partnerService] Error fetching partner 2 profile:', partner2Result.error);
    }
  }

  // RPC returns an array, get first element or null
  const partner1Data = partner1Result.data?.[0] || null;
  const partner2Data = partner2Result?.data?.[0] || null;

  return {
    data: {
      ...connection,
      partner_1: partner1Data,
      partner_2: partner2Data,
    },
    error: null,
  };
}

/**
 * Get the partner profile (the other user in the connection)
 */
export async function getPartnerProfile(pregnancyId: string, currentUserId: string) {
  const { data: connection, error } = await getPartnerConnection(pregnancyId);

  if (error || !connection || connection.status !== 'active') {
    return {
      data: null,
      error: error || { message: 'Kein Partner verbunden', code: 'NO_PARTNER' },
    };
  }

  // Determine which partner is the "other" one
  const isUser1 = connection.user_1_id === currentUserId;
  const partner = isUser1 ? connection.partner_2 : connection.partner_1;

  return { data: partner as PartnerProfile, error: null };
}

/**
 * Remove partner connection
 */
export async function removePartnerConnection(connectionId: string) {
  const { data, error } = await supabase
    .from('partner_connections')
    .update({ status: 'removed' })
    .eq('id', connectionId)
    .select()
    .single();

  return { data, error };
}

/**
 * Generate shareable invite link
 * Uses production URL for native apps to ensure links work correctly
 */
export function getInviteLink(token: string): string {
  const baseUrl = getAppBaseUrl();
  return `${baseUrl}/invite/${token}`;
}
